/* eslint-disable use-encapsulation/prefer-custom-hooks */
/* eslint-disable unicorn/no-null */
import type { ILatLng, IMBEStore } from '@/types';
import { Flex, Spinner, useControllableState } from '@chakra-ui/react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import type {
  BRHGeolocationPosition,
  BRHGeolocationPositionError,
  SomeCallback,
} from 'beautiful-react-hooks/shared/types';
import useGeolocationState from 'beautiful-react-hooks/useGeolocationState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreInfoWindow } from './StoreInfoWindow';
import debounce from 'lodash/debounce';

const DEBOUNCE_DELAY = 500;
const INITIAL_MAP_ZOOM_LEVEL = 11;

const createMarker = (options: google.maps.MarkerOptions): google.maps.Marker =>
  new google.maps.Marker(options);

interface IStoresMapProps {
  initialCenter?: ILatLng;
  onSelect?: (store: IMBEStore) => void;
  setVisibleStores: (stores: IMBEStore[]) => void;
  stores: IMBEStore[];
}

export const StoresMap: React.FC<IStoresMapProps> = ({
  initialCenter,
  onSelect,
  setVisibleStores,
  stores,
}) => {
  const { t } = useTranslation();

  // Workaround for performance warning issue
  const [libraries] = useState<
    ('places' | 'drawing' | 'geometry' | 'localContext' | 'visualization')[]
  >(['places']);

  const { isLoaded, loadError: hasError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
    libraries,
  });

  const { isRetrieving, isSupported, position } = useGeolocationState() as Readonly<{
    isRetrieving: boolean;
    isSupported: boolean;
    onError: (callback: SomeCallback<BRHGeolocationPositionError, void>) => void;
    position?: BRHGeolocationPosition;
  }>;

  const [mkr, setMarker] = useControllableState<google.maps.Marker | null>({ defaultValue: null });

  const [selectedStore, setStore] = useControllableState<IMBEStore | undefined>({
    defaultValue: undefined,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const latestClickedMarkerRef = useRef<google.maps.Marker | null>(null);

  const markers = useRef<Record<string, google.maps.Icon>>({
    unselected: {
      url: '/images/mbe-icon-system-pin.svg',
      scaledSize: new google.maps.Size(50, 50),
    },
    home: {
      url: '/images/mbe-icon-system-home.svg',
      scaledSize: new google.maps.Size(50, 50),
    },
    selected: {
      url: '/images/mbe-icon-system-pin-selected.svg',
      scaledSize: new google.maps.Size(50, 50),
    },
  });

  const getCenter = useCallback((): google.maps.LatLngLiteral => {
    if (initialCenter) return initialCenter;

    const defaultCenter = {
      lat: stores[0]?.gpsLocation.latitude ?? 0,
      lng: stores[0]?.gpsLocation.longitude ?? 0,
    };

    const isGeoLocationEnabled = isSupported && !isRetrieving && position?.coords;
    return isGeoLocationEnabled
      ? {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
      : defaultCenter;
  }, [isSupported, isRetrieving, position?.coords, stores, initialCenter]);

  const handleMarkerClick = (marker: google.maps.Marker, store: IMBEStore): void => {
    if (marker !== latestClickedMarkerRef.current) {
      infoWindowRef.current?.close();
      marker.setIcon(markers.current.selected);
      setStore(store);
      infoWindowRef.current?.open(mapRef.current, marker);
      latestClickedMarkerRef.current?.setIcon(markers.current.unselected);
      latestClickedMarkerRef.current = marker;
      setMarker(marker);
    }
  };

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setCenter(getCenter());
    }
  }, [getCenter]);

  const onLoad = (m: google.maps.Map): void => {
    const center = getCenter();
    m.setCenter(center);
    mapRef.current = m;

    createMarker({
      clickable: false,
      position: center,
      icon: markers.current.home,
      map: mapRef.current,
    });

    stores.forEach((store) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!store.gpsLocation?.latitude || !store.gpsLocation.longitude) return;

      const marker = createMarker({
        position: { lat: store.gpsLocation.latitude, lng: store.gpsLocation.longitude },
        icon: markers.current.unselected,
        map: mapRef.current,
      });

      marker.addListener('click', () => handleMarkerClick(marker, store));
    });
  };

  const onBoundsChanged = debounce(() => {
    const bounds = mapRef.current?.getBounds();
    const visibleStores: IMBEStore[] = [];

    for (const store of stores) {
      const {
        gpsLocation: { latitude, longitude },
      } = store;

      if (!latitude || !longitude) {
        continue;
      }

      const latLng = new google.maps.LatLng(latitude, longitude);

      if (bounds?.contains(latLng)) {
        visibleStores.push(store);
      }
    }

    setVisibleStores(visibleStores);
  }, DEBOUNCE_DELAY);

  const onLoadInfoWindow = (iw: google.maps.InfoWindow): void => {
    infoWindowRef.current = iw;
  };

  const handleInfoWindowClose = (): void => {
    infoWindowRef.current = null;
    latestClickedMarkerRef.current?.setIcon(markers.current.unselected);
    latestClickedMarkerRef.current = null;
    // eslint-disable-next-line unicorn/no-useless-undefined
    setStore(undefined);
  };

  const handleStoreSelection = (store: IMBEStore): void => {
    if (onSelect) onSelect(store);
  };

  if (hasError) {
    return <div>{t('STORE.MAP_LOAD_ERROR')}</div>;
  }

  if (!isLoaded)
    return (
      <Flex alignItems="center" justifyContent="center" w="full">
        <Spinner color="brand.red.dark" size="xl" />
      </Flex>
    );

  return (
    <GoogleMap
      mapContainerStyle={{ flexGrow: 1, height: '100%' }}
      options={{
        controlSize: 20,
        fullscreenControl: false,
      }}
      zoom={INITIAL_MAP_ZOOM_LEVEL}
      onBoundsChanged={onBoundsChanged}
      onLoad={onLoad}
    >
      {mkr && selectedStore && (
        <InfoWindow anchor={mkr} onCloseClick={handleInfoWindowClose} onLoad={onLoadInfoWindow}>
          <StoreInfoWindow store={selectedStore} onSelect={handleStoreSelection} />
        </InfoWindow>
      )}
    </GoogleMap>
  );
};
